/**
 * Start over, above Delete account in Settings → Profile (TMMAPI-0157, TMMAPP-0119 §E 26c): deletes
 * the person's accounts, imports, virtual accounts, envelopes, payees, plans and meetings, and keeps
 * the sign-in, the subscription, the AI wallet, billing history and consents. Confirmed by typing
 * the account's email (`POST /v1/me/data-reset {confirmation}`). On success: a toast of the served
 * counts (zeros omitted), this person's per-device onboarding keys cleared, `/me` refetched, and
 * My Accounts, empty, where onboarding starts again.
 */
import { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dataResetApi } from '@/api/dataReset';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { clearHowWeHelpHidden } from '@/features/automation/howWeHelpHidden';
import { useOptionalVirtualAccountsList } from '@/features/virtualAccounts/virtualAccountsListContext';
import { useLocale } from '@/i18n/useLocale';
import { useMe, useMeRefetch } from '@/session/meHooks';
import type { SettingsNotify } from './settingsHref';
import { startOverSummary } from './startOverSummary';

interface StartOverSectionProps {
  onNotify: SettingsNotify;
}

export function StartOverSection({ onNotify }: StartOverSectionProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const navigate = useNavigate();
  // The shell's shared virtual-accounts list: the reset emptied it (TMMAPP-0122 item 30).
  const virtualAccountsList = useOptionalVirtualAccountsList();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleReset() {
    const confirmation = email.trim();
    if (busy || confirmation === '') return;
    setBusy(true);
    setError(null);
    try {
      const result = await dataResetApi.reset(confirmation);
      const userId = me.user.id;
      clearHowWeHelpHidden(userId);
      onNotify(startOverSummary(t, result.counts, locale));
      setOpen(false);
      await Promise.all([refetchMe(), virtualAccountsList?.reload()]);
      void navigate('/me/accounts');
    } catch (failure) {
      // A frozen person can't reset right now (TMMAPI-0158, TMMAPP-0122 item 22).
      setError(
        isApiError(failure) && failure.code === ErrorCodes.SUBSCRIPTION_EXPIRED
          ? t('settings.startOver.locked')
          : isApiError(failure) && failure.code === ErrorCodes.USER_FROZEN
            ? t('settings.startOver.frozen')
            : errorCopy(t, failure, 'settings.errorGeneric'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box>
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Typography variant="body2">{t('settings.startOver.body')}</Typography>
        <Box>
          <Button
            color="error"
            variant="outlined"
            onClick={() => {
              setError(null);
              setEmail('');
              setOpen(true);
            }}
          >
            {t('settings.startOver.submit')}
          </Button>
        </Box>
      </Stack>
      {open && (
        <ConfirmDialog
          title={t('settings.startOver.confirmTitle')}
          consequence={t('settings.startOver.consequence')}
          confirmLabel={t('settings.startOver.submit')}
          destructive
          busy={busy}
          error={error}
          confirmDisabled={email.trim() === ''}
          onConfirm={() => void handleReset()}
          onCancel={() => setOpen(false)}
        >
          <Typography variant="body2">{t('settings.startOver.keeps')}</Typography>
          <TextField
            label={t('settings.confirmEmailLabel')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={busy}
            type="email"
            autoComplete="off"
            fullWidth
          />
        </ConfirmDialog>
      )}
    </Box>
  );
}
